import React from 'react';
import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import * as Lo from 'lodash';
import { useRootStore } from 'store';
import { useFormikContext } from 'formik';
import { promiseModalDialog, toastFailure } from 'motion-components';
import {
  CollectionOrderActions,
  CollectionOrderStates,
  DummyCollectorId,
} from 'shared-pages/shared/constants';
import { promiseModalOrderStatus } from 'shared-pages/pages/collection-orders/form/xrb-approval-tabs-flow/components/modal-orderstatus-promise';
import { prepareXrbForMutationData } from './use-order-xrb';
import { isPackagingStepNeeded } from './services/is-packaging-step-needed';
import { gql, useMutation } from '@apollo/client';

/** Πεδία που απαιτούνται για την ενέργεια "Ολοκλήρωση Οργάνωσης" (collection_orders_xrb 61-79) */
type RequiredFieldSpec = {
  path: string;
  type?: 'string' | 'number' | 'bool' | 'date';
  when?: (values: any) => boolean;
  validate?: (value: any, values: any) => boolean;
};

// Απαιτούμενα πεδία για την ενέργεια "Προγραμματισμός Συλλογής"
const REQUIRED_FIELDS_FOR_SCHEDULE_COLLECTOR_ORDER: RequiredFieldSpec[] = [
  { path: 'plannedCollectionDate', type: 'date' },
];

// Απαιτούμενα πεδία για την ενέργεια "Δημιουργία Συλλογής"
const REQUIRED_FIELDS_FOR_CREATE_COLLECTOR_ORDER: RequiredFieldSpec[] = [
  { path: 'collectorId', type: 'number', validate: (value) => Number(value) !== DummyCollectorId },
  { path: 'xrbData.temperatureReadingRequired', type: 'bool' },
  { path: 'xrbData.voltageReadingRequired', type: 'bool' },
  { path: 'xrbData.packagingTypeCode' },
  { path: 'xrbData.collordPackagingReturnRequired', type: 'bool' },
  { path: 'xrbData.wastereceptionpointId', type: 'number' },
];

// Απαιτούμενα πεδία για την ενέργεια "Ολοκλήρωση Αποστολής Συσκευασιών"
const REQUIRED_FIELDS_FOR_COMPLETE_PACKAGE_SHIPMENT: RequiredFieldSpec[] = [
  { path: 'xrbData.packagingInstructions' },
  { path: 'xrbData.packagingDispatchDate', type: 'date' },
];

// Απαιτούμενα πεδία για την ενέργεια "Ολοκλήρωση Οργάνωσης"
const REQUIRED_FIELDS_FOR_COMPLETE_ORGANIZATION: RequiredFieldSpec[] = [
  { path: 'xrbData.orgChemistry' },
  { path: 'xrbData.orgBatteryWeight', type: 'number' },
  { path: 'xrbData.orgPieceCount', type: 'number' },
  { path: 'xrbData.orgBatteryHeight', type: 'number' },
  { path: 'xrbData.orgBatteryLength', type: 'number' },
  { path: 'xrbData.orgBatteryWidth', type: 'number' },
  { path: 'xrbData.batteryCondition' },
  { path: 'xrbData.appropriatePackaging', type: 'bool' },
  { path: 'xrbData.requiredPackaging', when: (v) => v.xrbData?.appropriatePackaging === false },
  { path: 'xrbData.vermiculiteUsed', type: 'bool' },
  { path: 'xrbData.vermiculiteQuantity', when: (v) => v.xrbData?.vermiculiteUsed === true },
  { path: 'xrbData.aeroplastUsed', type: 'bool' },
  { path: 'xrbData.aeroplastQuantity', when: (v) => v.xrbData?.aeroplastUsed === true },
  { path: 'xrbData.plasticBagUsed', type: 'bool' },
  { path: 'xrbData.plasticBagQuantity', when: (v) => v.xrbData?.plasticBagUsed === true },
  { path: 'xrbData.polyethyleneUsed', type: 'bool' },
  { path: 'xrbData.polyethyleneQuantity', when: (v) => v.xrbData?.polyethyleneUsed === true },
  // { path: 'xrbData.otherMaterial', type: 'string' }, optional
  { path: 'xrbData.totalRequestWeight', type: 'number' },
];

/** Πεδία που απαιτούνται για την ενέργεια "Υποβολή Αιτήματος" */
const REQUIRED_FIELDS_FOR_SUBMIT_APPLICATION: RequiredFieldSpec[] = [
  // A1. Τεχνικές πληροφορίες για την μπαταρία
  { path: 'xrbData.pieceCount', type: 'number' },
  { path: 'xrbData.msdsFileId' },
  { path: 'xrbData.techSpecFileId' },
  { path: 'xrbData.bmsFileId' },
  { path: 'xrbData.manufacturer' },
  { path: 'xrbData.partCode' },
  { path: 'xrbData.chemistry' },
  { path: 'xrbData.cellGeometry' },
  { path: 'xrbData.nominalCapacity' },
  { path: 'xrbData.nominalVoltage' },
  { path: 'xrbData.nominalEnergyEfficiency' },
  { path: 'xrbData.batteryWeight', type: 'number' },
  { path: 'xrbData.batteryLength', type: 'number' },
  { path: 'xrbData.batteryWidth', type: 'number' },
  { path: 'xrbData.batteryHeight', type: 'number' },

  // A1.1 Στοιχεία ανά τεμάχιο μπαταρίας (ελέγχουμε το 1ο ως "ύπαρξη σχήματος")
  {
    path: 'xrbData.pieceDetails[0].daysOutOfOperation',
    type: 'number',
    when: (v) => Number(v?.xrbData?.pieceCount) >= 1,
  },
  {
    path: 'xrbData.pieceDetails[0].daysNotMoved',
    type: 'number',
    when: (v) => Number(v?.xrbData?.pieceCount) >= 1,
  },
  {
    path: 'xrbData.pieceDetails[0].chargeLevel',
    type: 'number',
    when: (v) => Number(v?.xrbData?.pieceCount) >= 1,
  },
  {
    path: 'xrbData.pieceDetails[0].chargeLevelDate',
    when: (v) => Number(v?.xrbData?.pieceCount) >= 1,
  },

  {
    path: 'xrbData.pieceDetails[1].daysOutOfOperation',
    type: 'number',
    when: (v) => Number(v?.xrbData?.pieceCount) >= 2,
  },
  {
    path: 'xrbData.pieceDetails[1].daysNotMoved',
    type: 'number',
    when: (v) => Number(v?.xrbData?.pieceCount) >= 2,
  },
  {
    path: 'xrbData.pieceDetails[1].chargeLevel',
    type: 'number',
    when: (v) => Number(v?.xrbData?.pieceCount) >= 2,
  },
  {
    path: 'xrbData.pieceDetails[1].chargeLevelDate',
    when: (v) => Number(v?.xrbData?.pieceCount) >= 2,
  },

  // A2. Προφυλάξεις ασφαλείας
  // { path: 'xrbData.cablesSecured' }, tri-state
  // { path: 'xrbData.safetyPlugInstalled' }, tri-state
  { path: 'xrbData.certifiedPackagingIsAvailable', type: 'bool' },
  {
    path: 'xrbData.certifiedPackagingCode',
    when: (v) => v?.xrbData?.certifiedPackagingIsAvailable !== false,
  },
  { path: 'xrbData.packagingCondition' },
  { path: 'xrbData.packagingHeight', type: 'number' },
  { path: 'xrbData.packagingLength', type: 'number' },
  { path: 'xrbData.packagingWidth', type: 'number' },
  { path: 'xrbData.replacementPackagingIsUsed', type: 'bool' },
  { path: 'xrbData.packagingReturnRequired', type: 'bool' },

  // B. Ιστορικό και οπτικός έλεγχος
  { path: 'xrbData.bmsCharacterization' },
  {
    path: 'xrbData.bmsCharacterizationOther',
    when: (v) => v?.xrbData?.bmsCharacterization === 'other',
  },
  { path: 'xrbData.carAccident', type: 'bool' },
  { path: 'xrbData.fire', type: 'bool' },
  { path: 'xrbData.naturalDisasters', type: 'bool' },
  { path: 'xrbData.vandalism', type: 'bool' },
  { path: 'xrbData.batteryLiquidCoolingSystemFailure', type: 'bool' },
  { path: 'xrbData.noBmsData', type: 'bool' },
  { path: 'xrbData.fireSmokeSignsSigns', type: 'bool' },
  { path: 'xrbData.oxidationSigns', type: 'bool' },
  { path: 'xrbData.leakageVentingSigns', type: 'bool' },
  { path: 'xrbData.cannotDiagnose', type: 'bool' },
  { path: 'xrbData.safetyDeviceTriggered', type: 'bool' },
  // { path: 'xrbData.additionalObservations' },
  { path: 'xrbData.technicianAssessment' },
  // { path: 'xrbData.packagingForCriticallyDamaged' }, tri-state

  // Γ. Πρόσβαση για παραλαβή/φόρτωση
  { path: 'xrbData.easyLoadingAccess', type: 'bool' },
  { path: 'xrbData.specialEquipmentRequired', type: 'bool' },
  { path: 'xrbData.collectionPointHasEquipment', type: 'bool' },
  { path: 'xrbData.equipmentDescription', when: (v) => !!v?.xrbData?.collectionPointHasEquipment },
  // { path: 'xrbData.otherAccessNotes' },

  // Δ. Φωτογραφικό υλικό (αποθηκεύονται στο xrbData.fileDetails μέσω FileRepoFieldList)
  { path: 'xrbData.fileDetails' },

  // Υπεύθυνη Δήλωση
  { path: 'xrbData.technicianVerifiesData', type: 'bool' },
  { path: 'xrbData.technicianName' },
  { path: 'xrbData.technicianRegistrationNumber' },
];

function isValueEmpty(value: unknown, type?: 'string' | 'number' | 'bool' | 'date'): boolean {
  if (type === 'bool') {
    return value === undefined || value === null;
  }
  if (type === 'number') {
    if (value === undefined || value === null || value === '') return true;
    const n = Number(value);
    return Number.isNaN(n);
  }
  if (type === 'date') {
    if (value === undefined || value === null || value === '') return true;
  }
  // string or default
  if (value === undefined || value === null) return true;
  if (typeof value === 'string') return value.trim() === '';
  return false;
}

export function validateRequiredFields(
  values: any,
  required: RequiredFieldSpec[],
): {
  valid: boolean;
  missingPaths: string[];
} {
  const missingPaths: string[] = [];
  for (const { path, type, when, validate } of required) {
    if (when && !when(values)) continue;
    const value = Lo.get(values, path);
    if (isValueEmpty(value, type)) {
      missingPaths.push(path);
      continue;
    }
    if (validate && !validate(value, values)) {
      missingPaths.push(path);
    }
  }
  return {
    valid: missingPaths.length === 0,
    missingPaths,
  };
}

export function validateRequiredForCompleteOrganization(values: any): {
  valid: boolean;
  missingPaths: string[];
} {
  return validateRequiredFields(values, REQUIRED_FIELDS_FOR_COMPLETE_ORGANIZATION);
}

// // Αιτήματα Συλλογής
// export enum CollectionOrderStates {
//   NotRouted = 1,
//   Routed = 2,
//   Executed = 3,
//   // Re Battery Liion specific states
//   ApplicationSubmitted = 4,
//   ApplicationRejected = 5,
//   OrganizationCompleted = 6,
//   PackageShipmentCompleted = 7,
//   CollectorOrder = 8,
//   ScheduledCollectorCollection = 9,
// }
// (1032, 1, 4, "Υποβολή Αίτησης", 1, CURDATE(), CURDATE(), "AUTO", "AUTO"),
// (1032, 2, 5, "Η Αίτηση Απορρίφθηκε", 1, CURDATE(), CURDATE(), "AUTO", "AUTO"),
// (1032, 3, 6, "Ολοκλήρωση Οργάνωσης", 1, CURDATE(), CURDATE(), "AUTO", "AUTO"),
// (1032, 4, 7, "Ολοκλήρωση Αποστολής Συσκευασιών", 1, CURDATE(), CURDATE(), "AUTO", "AUTO"),
// (1032, 5, 8, "Εντολή Συλλέκτη", 1, CURDATE(), CURDATE(), "AUTO", "AUTO"),
// (1032, 6, 9, "Προγραμματισμένη Συλλογή Συλλέκτη", 1, CURDATE(), CURDATE(), "AUTO", "AUTO"),

const modernBtnStyle = {
  marginLeft: '10px',
  height: '38px',
  minWidth: '180px',
  fontWeight: '500',
  letterSpacing: '0.3px',
  transition: 'all 0.3s ease',
  boxShadow: '0 2px 6px rgba(0, 0, 0, 0.12)',
};

// Modern button variants with borders
const btnVariants = {
  save: {
    ...modernBtnStyle,
    border: '2px solid #28a745',
    backgroundColor: 'transparent',
    color: '#28a745',
  },
  primary: {
    ...modernBtnStyle,
    border: '2px solid #007bff',
    backgroundColor: '#007bff',
    color: '#fff',
  },
  success: {
    ...modernBtnStyle,
    border: '2px solid #28a745',
    backgroundColor: '#28a745',
    color: '#fff',
  },
  danger: {
    ...modernBtnStyle,
    border: '2px solid #dc3545',
    backgroundColor: '#dc3545',
    color: '#fff',
  },
  dangerOutline: {
    ...modernBtnStyle,
    border: '2px solid #dc3545',
    backgroundColor: 'transparent',
    color: '#dc3545',
  },
  warning: {
    ...modernBtnStyle,
    border: '2px solid #ffc107',
    backgroundColor: '#ffc107',
    color: '#212529',
  },
  warningOutline: {
    ...modernBtnStyle,
    border: '2px solid #ffc107',
    backgroundColor: 'transparent',
    color: '#b38f00',
  },
};

export function useActionFunctions() {
  const { values, setValues, submitForm, setErrors, setTouched, touched, validateForm } =
    useFormikContext<any>();
  const { t } = useTranslation();
  const confirmAction = async (actionLabel: string): Promise<boolean> => {
    const ans = await promiseModalDialog({
      title: t('Επιβεβαίωση Ενέργειας'),
      text: `${t('Θέλετε να εκτελέσετε την ενέργεια')} <strong>${actionLabel}</strong>;`,
    });
    return !!ans;
  };
  const [rescheduleCollectionOrderPlannedDate] = useMutation(gql`
    mutation rescheduleCollectionOrderPlannedDate($id: Int, $date: DateTime) {
      rescheduleCollectionOrderPlannedDate(id: $id, date: $date)
    }
  `);
  const [updateCollectionOrder] = useMutation(gql`
    mutation updateCollectionOrder($data: CollectionOrderInput) {
      updateCollectionOrder(data: $data) {
        id
        statusId
      }
    }
  `);

  /** Optional callback to run extra manual (if-then) checks and push additional paths into missingPaths. */
  const validateAndBlockIfMissing = async (
    required: RequiredFieldSpec[],
    extraChecks?: (values: any, missingPaths: string[]) => void,
  ): Promise<boolean> => {
    const schemaErrors = await validateForm();
    const { valid, missingPaths } = validateRequiredFields(values, required);
    if (extraChecks) {
      extraChecks(values, missingPaths);
    }
    const hasMissing = missingPaths.length > 0;

    if (hasMissing) {
      const requiredMessage = t('Το πεδίο απαιτείται') || 'Το πεδίο απαιτείται';
      const actionErrors: any = {};
      const actionTouched: any = {};
      for (const path of missingPaths) {
        Lo.set(actionErrors, path, requiredMessage);
        Lo.set(actionTouched, path, true);
      }

      setErrors({ ...schemaErrors, ...actionErrors });
      setTouched({ ...touched, ...actionTouched });

      toastFailure(
        missingPaths.length === 1
          ? t('Συμπληρώστε το απαιτούμενο πεδίο.')
          : t('Συμπληρώστε τα απαιτούμενα πεδία ({{count}}).', { count: missingPaths.length }),
      );
      console.log('$$$ missingPaths', missingPaths);

      return false;
    }

    return true;
  };

  /** Προσωρινή Αποθήκευση */
  const handleSaveTemporary = async () => {
    setValues(
      Object.assign({}, values, {
        statusId: CollectionOrderStates.TemporarySaved,
        actionId: CollectionOrderActions.TemporarySaved,
      }),
    );
    console.log('action buttons approval flow handleSubmit');
    setTimeout(() => submitForm(), 0);
  };

  /** Υποβολή Αιτήματος */
  const handleSubmitApplication = async () => {
    if (!(await confirmAction(t('Υποβολή Αιτήματος')))) return;
    const ok = await validateAndBlockIfMissing(
      REQUIRED_FIELDS_FOR_SUBMIT_APPLICATION,
      (vals, missingPaths) => {
        // Τσέκαρε για το τικ στο Βεβαιώνω τα στοιχεία
        if (vals?.xrbData?.technicianVerifiesData !== true) {
          missingPaths.push('xrbData.technicianVerifiesData');
        }

        // Τσέκαρε αν υπάρχουν αρχεία για τα πεδία accessPhotoFileId, batteryPhotoFileId, packagingPhotoFileId, labelsPhotoFileId
        const fileDetails = vals?.xrbData?.fileDetails ?? [];
        const hasFileFor = (fieldName: string): boolean =>
          Array.isArray(fileDetails) &&
          fileDetails.some((row: any) => {
            if (row?.fieldName !== fieldName) return false;
            const fileId = row?.fileId;
            if (fileId === undefined || fileId === null || fileId === '') return false;
            const n = Number(fileId);
            return !Number.isNaN(n) && n > 0;
          });

        const requiredPhotoFields = [
          'accessPhotoFileId',
          'batteryPhotoFileId',
          'packagingPhotoFileId',
          'labelsPhotoFileId',
        ];

        const missingPhotoFields = requiredPhotoFields.filter((f) => !hasFileFor(f));
        // Πιστοποιητικό συσκευασίας υποχρεωτικό μόνο όταν έχει επιλεγεί critically damaged
        if (
          vals?.xrbData?.packagingForCriticallyDamaged &&
          !hasFileFor('packagingForCriticallyDamagedFileId')
        ) {
          missingPhotoFields.push('packagingForCriticallyDamagedFileId');
        }
        if (missingPhotoFields.length > 0) {
          // Anchor error on the array itself (safe) but keep unique paths per missing type.
          missingPaths.push('xrbData.fileDetails');
          for (const f of missingPhotoFields) {
            missingPaths.push(`xrbData.fileDetailsRequired.${f}`);
          }
        }
      },
    );
    if (!ok) return;

    // Θέσε status & αρχικοποίησε τα πεδία της οργάνωσης
    setValues(
      Object.assign({}, values, {
        statusId: CollectionOrderStates.ApplicationSubmitted,
        actionId: CollectionOrderActions.ApplicationSubmitted,
        orderDate: new Date(),
        // Αντέγραψε τα πεδία orgChemistry, orgBatteryWeight, orgPieceCount, orgBatteryHeight, orgBatteryLength, orgBatteryWidth
        xrbData: {
          ...values.xrbData,
          orgChemistry: values.xrbData.chemistry,
          orgBatteryWeight: values.xrbData.batteryWeight,
          orgPieceCount: values.xrbData.pieceCount,
          orgBatteryHeight: values.xrbData.batteryHeight,
          orgBatteryLength: values.xrbData.batteryLength,
          orgBatteryWidth: values.xrbData.batteryWidth,
          batteryCondition: values.xrbData.technicianAssessment,
          totalRequestWeight: (values.xrbData.batteryWeight * values.xrbData.pieceCount).toFixed(2),
        },
      }),
    );
    console.log('action buttons approval flow handleSubmitApplication');
    setTimeout(() => submitForm(), 0);
  };

  /** Απόρριψη Αιτήματος */
  const handleRejectApplication = async (
    event: React.MouseEvent<HTMLButtonElement>,
  ): Promise<void> => {
    if (!(await confirmAction(t('Απόρριψη Αιτήματος')))) return;
    setValues(
      Object.assign({}, values, {
        statusId: CollectionOrderStates.ApplicationRejected,
        actionId: CollectionOrderActions.ApplicationRejected,
      }),
    );
    console.log('action buttons approval flow handleRejectApplication');
    setTimeout(() => submitForm(), 0);
  };

  /** ΟΛΟΚΛΗΡΩΣΗ ΟΡΓΑΝΩΣΗΣ */
  const handleCompleteOrganization = async (
    event: React.MouseEvent<HTMLButtonElement>,
  ): Promise<void> => {
    if (!(await confirmAction(t('Ολοκλήρωση Οργάνωσης')))) return;
    console.log('handleCompleteOrganization values', values);
    const ok = await validateAndBlockIfMissing(REQUIRED_FIELDS_FOR_COMPLETE_ORGANIZATION);
    if (!ok) return;

    // Υπολογισμός Τύπου Συσκευασίας
    const condition = values.xrbData.batteryCondition;
    const packagingTypeCode =
      condition === 'used'
        ? 'P909'
        : condition === 'damaged'
        ? 'P908'
        : condition === 'critically_damaged'
        ? 'P911'
        : undefined;
    // Υπολογισμός Επιστροφή Συσκευασίας
    let collordPackagingReturnRequired = false;
    if (values.xrbData.appropriatePackaging === true) {
      collordPackagingReturnRequired = values.xrbData.packagingReturnRequired;
    } else if (values.xrbData.appropriatePackaging === false) {
      collordPackagingReturnRequired = null;
    }

    setValues(
      Object.assign({}, values, {
        statusId: CollectionOrderStates.OrganizationCompleted,
        actionId: CollectionOrderActions.OrganizationCompleted,
        // Αντέγραψε τα πεδία packagingTypeCode, collordPackagingReturnRequired, wastereceptionpointId
        xrbData: {
          ...values.xrbData,
          packagingTypeCode: packagingTypeCode,
          collordPackagingReturnRequired: collordPackagingReturnRequired,
        },
      }),
    );
    console.log('action buttons approval flow handleCompleteOrganization');
    setTimeout(() => submitForm(), 0);
  };

  /** ΟΛΟΚΛΗΡΩΣΗ ΑΠΟΣΤΟΛΗΣ ΣΥΣΚΕΥΑΣΙΩΝ */
  const handleCompletePackageShipment = async (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ): Promise<void> => {
    if (!(await confirmAction(t('Ολοκλήρωση Αποστολής Συσκευασιών')))) return;
    const ok = await validateAndBlockIfMissing(REQUIRED_FIELDS_FOR_COMPLETE_PACKAGE_SHIPMENT);
    if (!ok) return;

    setValues(
      Object.assign({}, values, {
        statusId: CollectionOrderStates.PackageShipmentCompleted,
        actionId: CollectionOrderActions.PackageShipmentCompleted,
      }),
    );
    console.log('action buttons approval flow handleCompletePackageShipment');
    setTimeout(() => submitForm(), 0);
  };

  /** Δημιουργία Εντολής Συλλογής i.e. Ανάθεση σε Συλλέκτη*/
  const handleCreateCollectorOrder = async (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ): Promise<void> => {
    if (!(await confirmAction(t('Εντολή προς Συλλέκτη')))) return;
    const ok = await validateAndBlockIfMissing(REQUIRED_FIELDS_FOR_CREATE_COLLECTOR_ORDER);
    if (!ok) return;

    setValues(
      Object.assign({}, values, {
        statusId: CollectionOrderStates.CollectorOrder,
        actionId: CollectionOrderActions.CollectorOrderCreated,
      }),
    );
    console.log('action buttons approval flow handleCreateCollectorOrder');
    setTimeout(() => submitForm(), 0);
  };

  /** Προγραμματισμός Συλλογής από τον Συλλέκτη */
  const handleCollectorScheduledCollection = async (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ): Promise<boolean> => {
    if (!(await confirmAction(t('Προγραμματίστηκε η Συλλογή')))) return false;
    const ok = await validateAndBlockIfMissing(REQUIRED_FIELDS_FOR_SCHEDULE_COLLECTOR_ORDER);
    if (!ok) return;
    setValues(
      Object.assign({}, values, {
        statusId: CollectionOrderStates.ScheduledCollectorCollection,
        actionId: CollectionOrderActions.ScheduledCollectorCollectionDefined,
      }),
    );
    console.log('action buttons approval flow handleCollectorScheduledCollection');
    setTimeout(() => submitForm(), 0);
  };

  /** Επαναπρογραμματισμός Συλλογής από τον Συλλέκτη */
  const handleCollectorRescheduleCollection = async (newDate: Date): Promise<any> => {
    // if (!(await confirmAction(t('Επαναπρογραμματισμός Συλλογής')))) return;
    setValues(
      Object.assign({}, values, {
        plannedCollectionDate: newDate,
      }),
    );
    console.log('action buttons approval flow handleCollectorRescheduleCollection');
    setTimeout(async () => {
      const result = await rescheduleCollectionOrderPlannedDate({
        variables: {
          id: values.id,
          date: newDate,
        },
      });
      submitForm();
    }, 0);
  };

  /** Ακύρωση Αιτήματος Συλλογής */
  const handleCancelCollectionOrder = async (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ): Promise<void> => {
    if (!(await confirmAction(t('Ακύρωση Αιτήματος')))) return;
    setValues(
      Object.assign({}, values, {
        statusId: CollectionOrderStates.ApplicationRejected,
        actionId: CollectionOrderActions.ApplicationRejected,
      }),
    );
    console.log('action buttons approval flow handleCancelCollectionOrder');
    setTimeout(() => submitForm(), 0);
  };

  /** Manual Αλλαγή Κατάστασης */
  const handleManualSetStatus = async (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ): Promise<void> => {
    // open modal (optionally with initial value / title)
    const result = await promiseModalOrderStatus({
      title: 'Αλλαγή της κατάστασης του Αιτήματος', // optional
      // initialStatusId: currentStatusId, // optional
    });

    if (result !== false) {
      if (!(await confirmAction(t('Αλλαγή Κατάστασης')))) return;
      // result is the selected status id (number)
      const statusId = result;
      const mutValues: any = {
        id: values?.id,
        statusId: statusId,
        actionId: CollectionOrderActions.StatusChangedManually,
        xrbData: values?.xrbData,
      };
      prepareXrbForMutationData({
        mutValues,
        isRebatteryLiion: true,
        isNew: false,
        idNum: values?.id,
      });
      console.log('StatusChangedManually');
      await updateCollectionOrder({
        variables: {
          data: mutValues,
        },
        refetchQueries: ['getCollectionOrderById', 'getCollectionOrderXrbByOrderId'],
        awaitRefetchQueries: true,
      });
    }
  };

  return {
    handleSaveTemporary,
    handleSubmitApplication,
    handleRejectApplication,
    handleCompleteOrganization,
    handleCompletePackageShipment,
    handleCreateCollectorOrder,
    handleCollectorScheduledCollection,
    handleCancelCollectionOrder,
    handleManualSetStatus,
    handleCollectorRescheduleCollection,
  };
}

/** BUTTONS APPROVAL FLOW */
export const ActionButtonsApprovalFlow = ({ isNew, updateRecord }) => {
  const { t } = useTranslation();
  const { appStore } = useRootStore();
  const { values, errors } = useFormikContext();
  const {
    handleSaveTemporary,
    handleSubmitApplication,
    handleRejectApplication,
    handleCompleteOrganization,
    handleCompletePackageShipment,
    handleCreateCollectorOrder,
    handleCollectorScheduledCollection,
    handleCancelCollectionOrder,
    handleManualSetStatus,
  } = useActionFunctions();

  const buttons = {
    save: ({ disabled }) => (
      <Button
        size="sm"
        style={btnVariants.save}
        disabled={disabled}
        variant="outline-success"
        onMouseEnter={(e) =>
          !disabled &&
          ((e.currentTarget.style.transform = 'translateY(-2px)'),
          (e.currentTarget.style.boxShadow = '0 4px 12px rgba(40, 167, 69, 0.3)'),
          (e.currentTarget.style.backgroundColor = 'rgba(40, 167, 69, 0.1)'))
        }
        onMouseLeave={(e) => (
          (e.currentTarget.style.transform = 'translateY(0)'),
          (e.currentTarget.style.boxShadow = '0 2px 6px rgba(0, 0, 0, 0.12)'),
          (e.currentTarget.style.backgroundColor = 'transparent')
        )}
        onClick={handleSaveTemporary}
      >
        Αποθήκευση στο Πρόχειρο
      </Button>
    ),

    applicationSubmitted: ({ disabled }) => (
      <Button
        size="sm"
        style={btnVariants.primary}
        disabled={disabled}
        onMouseEnter={(e) =>
          !disabled &&
          ((e.currentTarget.style.transform = 'translateY(-2px)'),
          (e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 123, 255, 0.3)'))
        }
        onMouseLeave={(e) => (
          (e.currentTarget.style.transform = 'translateY(0)'),
          (e.currentTarget.style.boxShadow = '0 2px 6px rgba(0, 0, 0, 0.12)')
        )}
        onClick={handleSubmitApplication}
      >
        Υποβολή Αιτήματος
      </Button>
    ),
    applicationRejected: ({ disabled }) => (
      <Button
        size="sm"
        variant="danger"
        style={btnVariants.danger}
        disabled={disabled}
        onMouseEnter={(e) =>
          !disabled &&
          ((e.currentTarget.style.transform = 'translateY(-2px)'),
          (e.currentTarget.style.boxShadow = '0 4px 12px rgba(220, 53, 69, 0.3)'))
        }
        onMouseLeave={(e) => (
          (e.currentTarget.style.transform = 'translateY(0)'),
          (e.currentTarget.style.boxShadow = '0 2px 6px rgba(0, 0, 0, 0.12)')
        )}
        onClick={handleRejectApplication}
      >
        Απόρριψη Αιτήματος
      </Button>
    ),
    organizationCompleted: ({ disabled }) => (
      <Button
        size="sm"
        style={btnVariants.success}
        disabled={disabled}
        onMouseEnter={(e) =>
          !disabled &&
          ((e.currentTarget.style.transform = 'translateY(-2px)'),
          (e.currentTarget.style.boxShadow = '0 4px 12px rgba(40, 167, 69, 0.3)'))
        }
        onMouseLeave={(e) => (
          (e.currentTarget.style.transform = 'translateY(0)'),
          (e.currentTarget.style.boxShadow = '0 2px 6px rgba(0, 0, 0, 0.12)')
        )}
        onClick={handleCompleteOrganization}
      >
        Ολοκλήρωση Οργάνωσης
      </Button>
    ),
    packageShipmentCompleted: ({ disabled }) => (
      <Button
        size="sm"
        style={btnVariants.success}
        disabled={disabled}
        onMouseEnter={(e) =>
          !disabled &&
          ((e.currentTarget.style.transform = 'translateY(-2px)'),
          (e.currentTarget.style.boxShadow = '0 4px 12px rgba(40, 167, 69, 0.3)'))
        }
        onMouseLeave={(e) => (
          (e.currentTarget.style.transform = 'translateY(0)'),
          (e.currentTarget.style.boxShadow = '0 2px 6px rgba(0, 0, 0, 0.12)')
        )}
        onClick={handleCompletePackageShipment}
      >
        Ολοκλήρωση Αποστολής Συσκευασιών
      </Button>
    ),
    collectorOrder: ({ disabled }) => (
      <Button
        size="sm"
        style={btnVariants.primary}
        disabled={disabled}
        onMouseEnter={(e) =>
          !disabled &&
          ((e.currentTarget.style.transform = 'translateY(-2px)'),
          (e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 123, 255, 0.3)'))
        }
        onMouseLeave={(e) => (
          (e.currentTarget.style.transform = 'translateY(0)'),
          (e.currentTarget.style.boxShadow = '0 2px 6px rgba(0, 0, 0, 0.12)')
        )}
        onClick={handleCreateCollectorOrder}
      >
        Εντολή προς Συλλέκτη
      </Button>
    ),
    scheduledCollectorCollection: ({ disabled }) => (
      <Button
        size="sm"
        style={btnVariants.primary}
        disabled={disabled}
        onMouseEnter={(e) =>
          !disabled &&
          ((e.currentTarget.style.transform = 'translateY(-2px)'),
          (e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 123, 255, 0.3)'))
        }
        onMouseLeave={(e) => (
          (e.currentTarget.style.transform = 'translateY(0)'),
          (e.currentTarget.style.boxShadow = '0 2px 6px rgba(0, 0, 0, 0.12)')
        )}
        onClick={handleCollectorScheduledCollection}
      >
        Προγραμματίστηκε η Συλλογή
      </Button>
    ),
    cancelCollectionOrder: ({ disabled }) => (
      <Button
        size="sm"
        style={btnVariants.warningOutline}
        disabled={disabled}
        variant="outline-warning"
        onMouseEnter={(e) =>
          !disabled &&
          ((e.currentTarget.style.transform = 'translateY(-2px)'),
          (e.currentTarget.style.boxShadow = '0 4px 12px rgba(255, 193, 7, 0.3)'))
        }
        onMouseLeave={(e) => (
          (e.currentTarget.style.transform = 'translateY(0)'),
          (e.currentTarget.style.boxShadow = '0 2px 6px rgba(0, 0, 0, 0.12)')
        )}
        onClick={handleCancelCollectionOrder}
      >
        Ακύρωση Αιτήματος
      </Button>
    ),
    manualSetStatus: ({ disabled }) => (
      <Button
        size="sm"
        style={btnVariants.dangerOutline}
        disabled={disabled}
        variant="outline-danger"
        onMouseEnter={(e) =>
          !disabled &&
          ((e.currentTarget.style.transform = 'translateY(-2px)'),
          (e.currentTarget.style.boxShadow = '0 4px 12px rgba(220, 53, 69, 0.3)'))
        }
        onMouseLeave={(e) => (
          (e.currentTarget.style.transform = 'translateY(0)'),
          (e.currentTarget.style.boxShadow = '0 2px 6px rgba(0, 0, 0, 0.12)')
        )}
        onClick={handleManualSetStatus}
      >
        Αλλαγή Κατάστασης
      </Button>
    ),
  };

  let visibleButtons = [];
  const statusId: CollectionOrderStates = (values as any)?.statusId ?? 0;
  const xrbData = (values as any)?.xrbData ?? {};
  const packagingStepIsNeeded = isPackagingStepNeeded(xrbData);

  // Σημείο Συλλογής
  if (appStore.isCollectionPoint) {
    if (isNew) {
      visibleButtons.push(...[buttons.save, buttons.applicationSubmitted]);
    } else if (statusId === CollectionOrderStates.TemporarySaved) {
      visibleButtons.push(...[buttons.save, buttons.applicationSubmitted]);
    } else return <></>;
  }
  // Εταιρεία Διαχείρισης
  else if (appStore.isPlatformOwner) {
    if (statusId === CollectionOrderStates.ApplicationSubmitted) {
      visibleButtons.push(
        ...[
          buttons.applicationRejected,
          buttons.organizationCompleted,
          // buttons.cancelCollectionOrder,
        ],
      );
    } else if (statusId === CollectionOrderStates.OrganizationCompleted) {
      if (packagingStepIsNeeded) {
        visibleButtons.push(...[buttons.packageShipmentCompleted, buttons.cancelCollectionOrder]);
      } else {
        visibleButtons.push(...[buttons.collectorOrder, buttons.cancelCollectionOrder]);
      }
    } else if (statusId === CollectionOrderStates.PackageShipmentCompleted) {
      visibleButtons.push(...[buttons.collectorOrder, buttons.cancelCollectionOrder]);
    } else if (statusId === CollectionOrderStates.CollectorOrder) {
      visibleButtons.push(...[buttons.cancelCollectionOrder]);
    }
    // Αλλαγή Κατάστασης Παντού
    visibleButtons.push(...[buttons.manualSetStatus]);
  }
  // Συλλέκτης
  else if (appStore.isCollector) {
    if (statusId === CollectionOrderStates.CollectorOrder) {
      visibleButtons.push(...[buttons.scheduledCollectorCollection]);
    }
  }

  if (visibleButtons.length == 0) {
    return <></>;
  }

  return (
    <div
      className="mt-1 mb-1 d-flex flex-column align-items-end"
      style={{
        padding: '8px 20px 16px 20px',
        border: '2px solid #e0e0e0',
        borderLeft: '5px solid #28a745',
        borderRadius: '8px',
        backgroundColor: '#f8f9fa',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
        marginLeft: 'auto',
        maxWidth: 'fit-content',
      }}
    >
      {/* DEBUG – αφαιρείς μετά τη δοκιμή */}
      {appStore.devMode && (
        <pre
          style={{
            fontSize: '11px',
            background: '#fff3cd',
            padding: 8,
            marginBottom: 8,
            borderRadius: 4,
            maxHeight: 120,
            overflow: 'auto',
            textAlign: 'left',
          }}
        >
          dev mode errors:{JSON.stringify(errors, null, 2)}
        </pre>
      )}

      <div
        className="text-center mb-2"
        style={{
          width: '100%',
          fontWeight: '600',
          fontSize: '14px',
          color: '#495057',
          letterSpacing: '0.5px',
        }}
      >
        Ενέργειες:
      </div>
      <div className="d-flex" style={{ gap: '4px' }}>
        {visibleButtons.map((button, index) => (
          <React.Fragment key={index}>{button({ disabled: false })}</React.Fragment>
        ))}
      </div>
    </div>
  );
};
